import type {Ref} from 'vue';
import {useLocalStorage} from '@vueuse/core';
import {z} from 'zod';
import * as Sentry from '@sentry/nuxt';
import {
	GUEST_MERGE_STATE_KEY,
	GUEST_SHOPPING_LISTS_KEY,
	MAX_GUEST_LISTS,
	MAX_STORAGE_BYTES,
	type GuestMergeState,
	type GuestShoppingListsState,
} from './types';

// Functional localStorage for guest shopping lists — same category as cart (necessary/functional),
// so no Cookiebot consent gate is required.

export const GUEST_LIST_GTM_ID_LENGTH = 12;

const GUEST_LIST_GTM_ID_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

export function createGuestListGtmId(): string {
	const bytes = new Uint8Array(GUEST_LIST_GTM_ID_LENGTH);
	crypto.getRandomValues(bytes);
	return Array.from(
		bytes,
		(byte) => GUEST_LIST_GTM_ID_ALPHABET[byte % GUEST_LIST_GTM_ID_ALPHABET.length],
	).join('');
}

const guestItemSchema = z.object({
	ID: z.number(),
	Code: z.string(),
	Text: z.string(),
	VANR: z.string().nullable().optional(),
	Amount: z.number().nullable().optional(),
	Purchased: z.boolean(),
	GeneralizedProduct: z.boolean(),
	LastUpdate: z.string(),
	LastUpdateBy: z.literal('guest'),
	VisualOrder: z.number().int().nullable().optional(),
	CreatedAt: z.string().optional(),
});

const guestListSchema = z.object({
	ID: z.number(),
	Name: z.string().min(1).max(256),
	ItemsCount: z.number().int().nonnegative(),
	IsSelected: z.boolean(),
	IsActive: z.literal(false),
	ApplicationType: z.literal('web-guest'),
	CreatedAt: z.string().optional(),
	gtmUuid: z.string().min(GUEST_LIST_GTM_ID_LENGTH).max(36).optional(),
	items: z.array(guestItemSchema),
});

const rootSchema = z.object({
	version: z.literal(1),
	idCounter: z.number().int().negative(),
	itemIdCounter: z.number().int().negative(),
	lists: z.array(guestListSchema).max(MAX_GUEST_LISTS),
	lastModified: z.string(),
});

export function getInitialGuestState(): GuestShoppingListsState {
	return {
		version: 1,
		idCounter: -1,
		itemIdCounter: -1,
		lists: [],
		lastModified: new Date().toISOString(),
	};
}

function parseGuestState(raw: string): GuestShoppingListsState {
	try {
		const parsed = JSON.parse(raw);
		if (parsed?.version === 1) {
			const validated = rootSchema.safeParse(parsed);
			if (validated.success) {
				const state = validated.data as GuestShoppingListsState;
				for (const list of state.lists) {
					if (!list.gtmUuid || list.gtmUuid.length !== GUEST_LIST_GTM_ID_LENGTH) {
						list.gtmUuid = createGuestListGtmId();
					}
					if (!list.CreatedAt) {
						const firstItemCreatedAt = list.items.find((item) => item.CreatedAt)?.CreatedAt;
						list.CreatedAt = firstItemCreatedAt
							?? list.items.find((item) => item.LastUpdate)?.LastUpdate
							?? state.lastModified;
					}
					for (const item of list.items) {
						if (!item.CreatedAt) {
							item.CreatedAt = item.LastUpdate;
						}
					}
				}
				return state;
			}
		}
		return getInitialGuestState();
	} catch {
		return getInitialGuestState();
	}
}

export function useGuestShoppingListStorage() {
	return useLocalStorage<GuestShoppingListsState>(
		GUEST_SHOPPING_LISTS_KEY,
		getInitialGuestState(),
		{
			listenToStorageChanges: true,
			serializer: {
				read: parseGuestState,
				write: (value) => {
					const serialized = JSON.stringify(value);
					if (serialized.length > MAX_STORAGE_BYTES) {
						Sentry.captureMessage('Guest shopping lists storage exceeds size limit', {
							level: 'warning',
							extra: {bytes: serialized.length},
						});
						throw new Error('GUEST_SHOPPING_LISTS_SIZE_EXCEEDED');
					}
					return serialized;
				},
			},
		},
	);
}

export function hasGuestData(state: GuestShoppingListsState): boolean {
	return (state.lists[0]?.items.length ?? 0) > 0;
}

export function clearGuestStorage(storage: Ref<GuestShoppingListsState>) {
	storage.value = getInitialGuestState();
}

export function getGuestMergeState(): GuestMergeState | null {
	if (!import.meta.client) {
		return null;
	}
	const value = sessionStorage.getItem(GUEST_MERGE_STATE_KEY);
	if (value === 'pending' || value === 'done' || value === 'dismissed') {
		return value;
	}
	return null;
}

export function setGuestMergeState(state: GuestMergeState) {
	if (!import.meta.client) {
		return;
	}
	sessionStorage.setItem(GUEST_MERGE_STATE_KEY, state);
}

export function clearGuestMergeState() {
	if (!import.meta.client) {
		return;
	}
	sessionStorage.removeItem(GUEST_MERGE_STATE_KEY);
}

export function resetGuestMergeStateIfNewGuestData(state: GuestShoppingListsState) {
	if (!import.meta.client) {
		return;
	}
	const mergeState = getGuestMergeState();
	if ((mergeState === 'done' || mergeState === 'dismissed') && hasGuestData(state)) {
		clearGuestMergeState();
	}
}

export function shouldShowMergeDialog(state: GuestShoppingListsState): boolean {
	if (!import.meta.client) {
		return false;
	}
	const mergeState = getGuestMergeState();
	if (mergeState === 'done' || mergeState === 'dismissed') {
		return false;
	}
	return hasGuestData(state);
}
